import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Device } from '@capacitor/device';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { App } from '@capacitor/app';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Container, Button, Typography, CircularProgress, Box, Link, Drawer, IconButton, AppBar, Toolbar, Avatar, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Close as CloseIcon, Check as CheckIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import PrivacyPolicyPage from '../PrivacyPolicyPage';
import useDeviceInfo from '../../utils/useDeviceInfo';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import { useTranslation } from 'react-i18next'; // Importer le hook i18n

const entitlementIdentifier = process.env.REACT_APP_ENTITLEMENT_IDENTIFIER;

const CheckoutPage = () => {
  const { t } = useTranslation(); // Hook pour les traductions
  const [products, setProducts] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [isPrivacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [isPurchaseActive, setIsPurchaseActive] = useState(false); // Nouvel état pour suivre l'état de l'achat
  const navigate = useNavigate();
  const { currentUser, refreshUser } = useAuth();
  const { paddingTop, paddingBottom } = useDeviceInfo();

  const handleBackClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    const configurePurchases = async () => {
      if (!currentUser) {
        console.error('Current user is not defined');
        return;
      }

      try {
        const info = await Device.getInfo();
        const apiKey = info.platform === 'ios'
          ? process.env.REACT_APP_REVENUECAT_IOS_API_KEY
          : process.env.REACT_APP_REVENUECAT_ANDROID_API_KEY;

        if (!apiKey) {
          console.error('API key is missing for platform:', info.platform);
          return;
        }

        const appUserID = currentUser.userId;

        await Purchases.configure({ apiKey, appUserID });

        const offerings = await Purchases.getOfferings();
        setProducts(offerings.current.availablePackages);
      } catch (error) {
        console.error('Error configuring purchases:', error);
      }
    };

    configurePurchases();

    const handleAppStateChange = (state) => {
      console.log('App state change:', state);
      if (state.isActive && isPurchaseActive) {
        setIsPurchaseActive(false);
        navigate('/status');
      }
    };

    App.addListener('appStateChange', handleAppStateChange);

    return () => {
      App.removeListener('appStateChange', handleAppStateChange);
    };
  }, [currentUser, navigate, refreshUser, isPurchaseActive]);

  const handlePurchase = async (product) => {
    setPurchasing(true);
    try {
      setIsPurchaseActive(true); // Indiquer que l'achat est en cours
      const purchaseResult = await Purchases.purchasePackage({ aPackage: product });
      if (purchaseResult.customerInfo.entitlements.active[entitlementIdentifier]) {
        console.log('Achat réussi');
        await refreshUser();
        if (currentUser.subscriptionStatus === 'active') {
          navigate('/status');
        }
      }
    } catch (error) {
      if (error.code === Purchases.PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        console.log('Achat annulé');
      } else {
        console.error('Erreur lors de l\'achat:', error);
      }
    } finally {
      setPurchasing(false);
      setIsPurchaseActive(false); // Réinitialiser l'état de l'achat
    }
  };

  const handlePrivacyPolicyOpen = () => {
    setPrivacyPolicyOpen(true);
  };

  const handlePrivacyPolicyClose = () => {
    setPrivacyPolicyOpen(false);
  };

  return (
    <>
      {/* Toolbar avec bouton de retour */}
      <AppBar position="static" sx={{ backgroundColor: 'transparent', boxShadow: 'none' }}>
        <Toolbar sx={{ pt: paddingTop + 2, px: 2 }}>
          <IconButton
            size="small"
            sx={{
              p: 0.5,
              border: '1px solid #ddd',
              borderRadius: '50%',
              mr: 2,
            }}
            aria-label="retour"
            onClick={handleBackClick}
          >
            <ArrowBackIosRoundedIcon fontSize="small" />
          </IconButton>
        </Toolbar>
      </AppBar>

    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '94vh',
        textAlign: 'center',
        position: 'relative',
      }}
    >

      <Box component="img" src="/img/logo.svg" alt="Logo" width="28vw" sx={{ mb:3 }} />

      <Box>
          <>
            <Box sx={{ mb: 4}} >
          {/* Section Titre et Accroche Duo */}
          <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 2, mb: 2 }}>
            {t('meditate_in_pair')}
          </Typography>
          <Box sx={{ fontWeight: 'bold', mb: 2, backgroundColor: 'secondary.main', px: 3, py: 2, borderRadius:2, margin:'0 auto' }}>
            <Typography variant="h4" sx={{ marginBottom:0.5, fontSize:"1.8em" }}>
              3.99€ {t('per_month')}
            </Typography>
            <Typography variant="body" sx={{ fontWeight: 'bold' }}>
              1 {t('subscription')} = 2 {t('accesses')}
            </Typography>
          </Box>

            </Box>
            <Box sx={{ textAlign: 'left', mb: 3, px: 1 }}>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }} >
              <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 32, height: 32 }}>
                <CheckRoundedIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </Avatar>
              <Typography variant="body1">
                {t('subscription_arg_1')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }} >
              <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 32, height: 32 }}>
                <CheckRoundedIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </Avatar>
              <Typography variant="body1">
                {t('subscription_arg_2')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }} >
              <Avatar sx={{ bgcolor: 'secondary.main', mr: 2, width: 32, height: 32 }}>
                <CheckRoundedIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </Avatar>
              <Typography variant="body1">
                {t('subscription_arg_3')}
              </Typography>
            </Box>

              <Button
                variant="contained"
                sx={{ mt: 2.5,mb: 2, fontSize: '1.2em' }}
                color="primary"
                fullWidth
                onClick={() => handlePurchase(products[0])}
                disabled={purchasing}
              >
                {purchasing ? <CircularProgress size={24} /> : t('start_trial')}
              </Button>
              <Typography variant="body2" sx={{ textAlign: 'center', fontStyle:'italic', fontSize: '12px', width:'80%', margin:'0 auto' }}>
                {t('auto_renewable')}
              </Typography>
              <Box sx={{ mt: 1.5, textAlign:'center', display:'flex', flexDirection:'row', justifyContent:'center' }}>
                <Link
                  href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                  target="_blank"
                  rel="noopener"
                  sx={{ fontSize: '12px', textDecoration: 'underline', px:0.5, mb:1 }}
                >
                  {t('terms_of_service')}
                </Link>
              </Box>
            </Box>
          </>
      </Box>
      <Drawer
        anchor="bottom"
        open={isPrivacyPolicyOpen}
        onClose={handlePrivacyPolicyClose}
      >
        <Box sx={{ width: '100%', p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', height: '50px' }}>
            <IconButton onClick={handlePrivacyPolicyClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ px: 3}}>
            <PrivacyPolicyPage />
          </Box>
        </Box>
      </Drawer>
    </Container>
    </>
  );
};

export default CheckoutPage;
